import {
  cardDismissalLabel,
  decodeActionCard,
  isInformationalActionType,
} from '@littlebird/action-cards';
import { cardState } from '@littlebird/action-cards-view';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { XStack } from '@/components/ui/layout/layout';
import { providerIcon } from '@/features/action-cards/icons';
import { logger } from '@/lib/logger';
import { openUntrustedUrl } from '@/utils/external-links';

import { CARD_FRAME } from './cardFrame';
import { FeedIcon } from './feed/FeedIcon';
import { FeedOpenLink } from './feed/FeedOpenLink';
import { FEED_ROW_GRID } from './feed/feedStyles';
import { FeedTile } from './feed/FeedTile';

import type { WebSurface } from './analytics';
import type { ActionCard } from '@littlebird/sdk';

type FallbackReason = 'not_computed' | 'no_view';

const STATUS_TEXT: Record<ActionCard['status'], string> = {
  draft: 'Waiting for you',
  running: 'Running',
  completed: 'Done',
  completed_externally: 'Done',
  failed: "Couldn't send",
  dismissed: 'Dismissed',
  revoked: 'Revoked',
};

const loggedFallbacks = new Set<string>();

const fallbackReasonOf = (card: ActionCard): FallbackReason =>
  card.computed === null ? 'not_computed' : 'no_view';

// Shown when the surface has no usable view: the server did not compute the card, or no document
// serves its type. An old catalog gets the update-prompt document, so it never reaches this fallback
export const CardFallback = observer(function CardFallback({
  card,
  surface,
}: {
  card: ActionCard;
  surface: WebSurface;
}) {
  // Views arrive by sync on the same MobX card, so React Compiler must not cache this
  'use no memo';
  const decoded = decodeActionCard(card);
  const state = cardState(card.row);
  const reason = fallbackReasonOf(card);
  const provider = decoded?.provider;
  const littlebird =
    !provider ||
    provider.id === 'Littlebird' ||
    isInformationalActionType(card.actionType);
  const appName = provider?.label ?? 'browser';
  const openUrl =
    state.open_url ?? decoded?.externalUrl ?? decoded?.sourceLink?.href;

  const row = surface !== 'web_chat';

  useEffect(() => {
    const key = `${surface}:${card.id}`;
    if (loggedFallbacks.has(key)) return;
    loggedFallbacks.add(key);
    logger.warn(
      `[ACTION_CARDS] no usable view for ${surface}, showing the fallback`,
      { cardId: card.id, actionType: card.actionType, surface, reason }
    );
  }, [card.id, card.actionType, surface, reason]);

  const tile = (
    <FeedTile
      glyph={littlebird ? providerIcon.Littlebird : providerIcon[provider.id]}
      label={littlebird ? 'Littlebird' : provider.label}
      littlebird={littlebird}
    />
  );
  const title =
    state.row_title ?? (card.title || decoded?.summary || 'Action card');
  const statusText =
    card.status === 'dismissed'
      ? cardDismissalLabel(card)
      : STATUS_TEXT[card.status];
  const openLink = openUrl ? (
    <FeedOpenLink
      url={openUrl}
      appName={appName}
      onOpenUrl={openUntrustedUrl}
    />
  ) : null;

  if (row)
    return (
      <div className={FEED_ROW_GRID} data-testid="card-fallback">
        <FeedIcon failed={card.status === 'failed'}>{tile}</FeedIcon>
        <span className="flex min-w-0 flex-col">
          <span className="min-w-0 truncate text-[length:var(--ac-row-title)] leading-[var(--ac-row-title-lh)] text-foreground">
            {title}
          </span>
          <span className="min-w-0 truncate text-[length:var(--ac-row-meta)] leading-[var(--ac-row-meta-lh)] text-foreground-muted">
            {statusText}
          </span>
        </span>
        <span className="flex items-center">{openLink}</span>
      </div>
    );

  return (
    <div className={CARD_FRAME} data-testid="card-fallback">
      <XStack className="min-w-0 items-center gap-[var(--ac-gap-sm)]">
        {tile}
        <span className="min-w-0 flex-1 truncate text-[length:var(--ac-title)] leading-[var(--ac-title-lh)] text-foreground">
          {title}
        </span>
        <span className="shrink-0 whitespace-nowrap text-[length:var(--ac-meta)] leading-[var(--ac-meta-lh)] text-foreground-muted">
          {statusText}
        </span>
        {openLink}
      </XStack>
    </div>
  );
});
